import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { toast, Toaster } from "sonner";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error("Please login to contact us");
      return;
    }

    setLoading(true);
    setResponseMessage("");

    try {
      const res = await axios.post(
        "https://wavetechservices.in/api/user/contactus", 
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setResponseMessage(res.data.message);
      setFormData({ fullName: "", email: "", phone: "", message: "" });
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.error || "Something went wrong!";
      setResponseMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <Toaster position="top-center" richColors />
      
      <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">Contact Us</h1>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Contact Form */}
        <Card className="w-full lg:w-1/2 p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 text-center">Send us a message</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="Enter your Name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="Enter your Email"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="Enter your Phone Number"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="How can we help you?"
                required
              ></textarea>
            </div>
            
            <div className="pt-2">
              <Button 
                type="submit" 
                className="w-full gradient py-2 px-6 text-base font-medium"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : "Submit"}
              </Button>
            </div>
            
            {responseMessage && (
              <p className={`mt-3 text-sm ${responseMessage.includes("wrong") ? "text-red-600" : "text-green-600"}`}>
                {responseMessage}
              </p>
            )}
          </form>
        </Card>

        {/* Help Information */}
        <div className="w-full lg:w-1/2 space-y-6">
          <Card className="p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-2">Need help?</h2>
            <p className="text-gray-600">
              For any immediate help regarding your bookings, please log-in and visit our Help Center.
            </p>
          </Card>
          
          <Card className="p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-2">Still facing issues?</h2>
            <p className="text-gray-600 mb-2">
              If you need more help, please send us an email at:
            </p>
            <a href="mailto:info@wave.com" className="text-blue-600 hover:underline">
              info@wavetechservice.in
            </a>
            <p>Contact Number : +91 9900006581</p>
          </Card>
          
          <Card className="p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-2">Our office address</h2>
            <a href="https://www.google.com/maps/place/Hotel+Virat/@13.0107106,77.5478121,17z/data=!4m9!3m8!1s0x3bae3def38b865b3:0xd66a8f59c465fc8e!5m2!4m1!1i2!8m2!3d13.0107106!4d77.550387!16s%2Fg%2F11n0b_54vv?entry=ttu&g_ep=EgoyMDI1MDQyMC4wIKXMDSoASAFQAw%3D%3D">
              <p className=" mb-3 text-blue-600 hover:underline font-medium ">
                No. 149/A, Hotel Virat, 3rd Floor, Iskon Foothills, 20th Main, 1st R Block, Rajajinagar, Bengaluru - 560010
              </p>
            </a>
            {/* <button className="text-blue-600 hover:underline font-medium">
              View All Addresses
            </button> */}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;